import { useState } from "react";
import type { JobRun } from "@alpha/shared";
import { ApiError, api, errorText } from "../api";
import { Chip, Empty, ErrorBox, Loading, PageHeader } from "../components/ui";
import { formatDateTime } from "../env";
import { useAsync } from "../useAsync";

export function JobsPage() {
  const runs = useAsync((s) => api.get<{ items: JobRun[] }>("/v1/jobs?take=50", s), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function retry(id: string) {
    setBusyId(id);
    setActionError(null);
    try {
      await api.post(`/v1/jobs/${id}/retry`);
      runs.reload();
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusyId(null);
    }
  }

  if (runs.loading) return <Loading />;
  if (runs.error instanceof ApiError && runs.error.status === 403) {
    return (
      <section>
        <PageHeader title="Jobs" />
        <Empty>Job runs are visible to administrators only.</Empty>
      </section>
    );
  }
  if (runs.error || !runs.data) return <ErrorBox error={runs.error} onRetry={runs.reload} />;

  return (
    <section>
      <PageHeader title="Jobs">
        <button type="button" className="btn small" onClick={runs.reload}>
          Refresh
        </button>
      </PageHeader>
      {actionError && (
        <p role="alert" className="error">
          {actionError}
        </p>
      )}
      <div className="card">
        {runs.data.items.length === 0 ? (
          <Empty>No job runs recorded yet.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th>Queued</th>
                <th>Started</th>
                <th>Finished</th>
                <th>Error</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {runs.data.items.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>
                    <Chip value={r.status} />
                  </td>
                  <td className="num">{formatDateTime(r.createdAt)}</td>
                  <td className="num">{r.startedAt ? formatDateTime(r.startedAt) : ""}</td>
                  <td className="num">{r.finishedAt ? formatDateTime(r.finishedAt) : ""}</td>
                  <td className="muted clip" title={r.error ?? undefined}>
                    {r.error ?? ""}
                  </td>
                  <td className="right">
                    {r.status === "failed" && (
                      <button type="button" className="btn small" disabled={busyId === r.id} onClick={() => void retry(r.id)}>
                        {busyId === r.id ? "Retrying..." : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
